import { useQuery } from '@tanstack/react-query';
import { useState, useEffect, useRef } from 'react';

/**
 * Hook to fetch, process, and prepare iframe content from a URL
 * Uses React Query to handle loading states and errors
 */
export const useIframeContentProcessor = (iframeUrl) => {
    const [blobUrl, setBlobUrl] = useState(null);
    const [contentHeight, setContentHeight] = useState(null);

    // Use React Query to fetch the content
    const { data: content, isLoading, error } = useQuery({
        queryKey: ['iframeContent', iframeUrl],
        queryFn: async () => {
            if (!iframeUrl) return null;
            const response = await fetch(iframeUrl, {
                credentials: 'include', // Include cookies for authentication
                headers: {
                    'Accept': 'text/html,application/xhtml+xml,application/xml',
                },
            });

            if (!response.ok) {
                throw new Error(`Failed to fetch content: ${response.status} ${response.statusText}`);
            }

            const content = await response.text();

            return content;
        },
        enabled: !!iframeUrl, // Only run the query if iframeUrl exists
        refetchOnWindowFocus: false,
        refetchOnMount: false,

    });

    // Process the content and create a blob URL when content is available
    useEffect(() => {
        if (!content) return;

        // Extract the base URL from the iframe URL dynamically
        const urlObj = new URL(iframeUrl);
        const baseUrl = `${urlObj.protocol}//${urlObj.host}`;
        console.log('Using base URL:', baseUrl);

        // Process content to fix relative URLs
        let processedContent = content;

        // Fix all relative URLs in the content (src, href attributes)
        processedContent = processedContent.replace(/(src|href)=["'](\/?static\/[^"']*)["']/g, (match, attr, url) => {
            // If URL starts with a slash, append it to base URL
            if (url.startsWith('/')) {
                return `${attr}="${baseUrl}${url}"`;
            }
            // Otherwise, assume it's relative to the base URL
            return `${attr}="${baseUrl}/${url}"`;
        });

        // Fix other relative URLs that might be in the content
        processedContent = processedContent.replace(/(src|href)=["'](\/[^"']*)["']/g, (match, attr, url) => {
            if (url.startsWith('/')) {
                return `${attr}="${baseUrl}${url}"`;
            }
            return match;
        });

        // Add a base tag to help resolve any remaining relative URLs
        if (!processedContent.includes('<base')) {
            processedContent = processedContent.replace('<head>', `<head>\n<base href="${baseUrl}">\n`);
        }

        // Create a blob from the modified content
        const blob = new Blob([processedContent], { type: 'text/html' });
        const newBlobUrl = URL.createObjectURL(blob);
        setBlobUrl(newBlobUrl);

        // Dynamically create a hidden iframe to measure content height
        const iframe = document.createElement('iframe');
        iframe.style.visibility = 'hidden';
        iframe.style.position = 'absolute';
        iframe.style.width = '1000px'; // Large width to avoid wrapping
        iframe.style.height = '10px';
        iframe.style.pointerEvents = 'none';
        iframe.src = newBlobUrl;
        document.body.appendChild(iframe);

        const onLoad = () => {
            try {
                const doc = iframe.contentDocument || iframe.contentWindow.document;
                const body = doc.body;
                const html = doc.documentElement;
                // Get the max height of body and html
                const height = Math.max(
                    body ? body.scrollHeight : 0,
                    html ? html.scrollHeight : 0
                );
                setContentHeight(height);
            } catch (e) {
                setContentHeight(null);
            }
            // Clean up the iframe after measuring
            setTimeout(() => {
                document.body.removeChild(iframe);
            }, 0);
        };
        iframe.onload = onLoad;

        // Clean up blob URL when component unmounts or URL changes
        return () => {
            URL.revokeObjectURL(newBlobUrl);
            if (iframe && document.body.contains(iframe)) {
                document.body.removeChild(iframe);
            }
        };
    }, [content, iframeUrl]);

    return {
        blobUrl,
        isLoading,
        error,
        isSuccess: !!blobUrl && !isLoading && !error,
        contentHeight,
    };
};

export default useIframeContentProcessor;
